import React, { useState, useEffect, useRef } from "react";
import close from "../../assets/icons/close.svg";
import { Postdata } from "../../API/GlobalApi";
import Notifier from "../../Utils/notify";
import { useFocusTrap } from "../../Utils/TabIndexingFocus";

const ChangeStudentContactNumber = ({ show, handleClose, studentId, oldNumber, onSubmit, fetchStudents, currentPage }) => {
    const [newContact, setNewContact] = useState("");
    const [error, setError] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [otp, setOtp] = useState(Array(6).fill(""));
    const [otpError, setOtpError] = useState("");
    const [loading, setLoading] = useState(false);
    const otpInputs = useRef([]);
    const trapRef = useFocusTrap(show);

    // Reset modal state when opened
    useEffect(() => {
        if (show) resetModal();
    }, [show]);

    const resetModal = () => {
        setNewContact("");
        setError("");
        setOtpSent(false);
        setOtp(Array(6).fill(""));
        setOtpError("");
        setLoading(false);
    };

    // Send OTP
    const handleSendOtp = async () => {
        if (!newContact.trim()) return setError("New contact number is required");
        if (newContact.length !== 10) return setError("Contact number must be 10 digits");

        setError("");
        setLoading(true);

        try {
            const res = await Postdata("students/student-admission/send-sms/", {
                student_id: studentId,
                phone_number: Number(newContact),
            });

            if (res?.otp) {
                setOtpSent(true);
                setTimeout(() => otpInputs.current[0]?.focus(), 100);
                Notifier.success(res?.message);
            } else {
                Notifier.error(res?.error || "Failed to send OTP");
                setError(res?.message || "Failed to send OTP");
            }
        } catch (err) {
            setError("Failed to send OTP. Try again.");
        } finally {
            setLoading(false);
        }
    };

    // OTP input handling
    const handleOtpChange = (e, index) => {
        const val = e.target.value;
        if (/^[0-9]?$/.test(val)) {
            const updatedOtp = [...otp];
            updatedOtp[index] = val;
            setOtp(updatedOtp);

            if (val && index < otp.length - 1) otpInputs.current[index + 1]?.focus();
            if (!val && index > 0) otpInputs.current[index - 1]?.focus();
        }
    };

    // Verify OTP
    const handleVerifyOtp = async () => {
        const otpString = otp.join("");
        if (otpString.length !== 6) return setOtpError("Please enter complete 6-digit OTP");

        setOtpError("");
        setLoading(true);

        try {
            const res = await Postdata("students/student-admission/verify-sms/", {
                student_id: studentId,
                phone_number: newContact,
                otp: otpString,
            });

            if (res?.error) Notifier.error(res.error);
            else if (res?.message === "Phone number verified successfully.") {
                onSubmit({ studentId, newContact });

                if (fetchStudents) {
                    fetchStudents({ page: currentPage });
                }

                handleClose();
            } else setOtpError(res?.message || "Invalid OTP");
        } catch {
            setOtpError("Failed to verify OTP. Try again.");
        } finally {
            setLoading(false);
        }
    };

    // ⭐ NEW: Form submit handler (replaces Enter-key useEffect)
    const handleFormSubmit = (e) => {
        e.preventDefault();
        otpSent ? handleVerifyOtp() : handleSendOtp();
    };

    if (!show) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content-success" style={{ maxWidth: "450px" }} ref={trapRef}>
                <div className="modal-header-content d-flex justify-content-between align-items-center mb-3">
                    <h4 className="text-primary m-0">Change Contact Number</h4>
                    <button className="close-btn" onClick={handleClose}>
                        <img src={close} alt="close" />
                    </button>
                </div>

                {/* ⭐ FORM WRAPPER ADDED */}
                <form onSubmit={handleFormSubmit}>
                    <div className="mb-3">
                        <label className="form-label">Old Contact Number</label>
                        <input type="text" className="form-control" value={oldNumber || ""} disabled />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">New Contact Number</label>
                        <input
                            type="text"
                            className="form-control"
                            value={newContact}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (/^\d{0,10}$/.test(val)) setNewContact(val);
                            }}
                            placeholder="Enter new number"
                            disabled={otpSent}  // ⭐ SHOW + DISABLE AFTER OTP SENT
                        />
                    </div>

                    {error && <p className="text-danger mb-2">{error}</p>}

                    {!otpSent ? (
                        <div className="d-flex justify-content-end gap-2 mt-3">
                            <button className="btn btn-secondary" type="button" onClick={handleClose}>Cancel</button>
                            <button className="add-btn" type="submit" disabled={loading}>
                                {loading ? <span className="spinner-border spinner-border-sm" role="status" /> : "Send OTP"}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="otp-input" style={{ display: "flex", justifyContent: "center", marginBottom: "15px" }}>
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        type="text"
                                        value={digit}
                                        ref={(el) => (otpInputs.current[index] = el)}
                                        onChange={(e) => handleOtpChange(e, index)}
                                        maxLength="1"
                                        style={{
                                            width: "40px",
                                            height: "40px",
                                            textAlign: "center",
                                            margin: "0 5px",
                                            fontSize: "16px",
                                        }}
                                        required
                                    />
                                ))}
                            </div>

                            {otpError && <p className="text-danger mb-2">{otpError}</p>}

                            <div className="d-flex justify-content-end gap-2 mt-3">
                                <button className="btn btn-secondary" type="button" onClick={handleClose}>Cancel</button>
                                <button className="add-btn" type="submit" disabled={loading}>
                                    {loading ? <span className="spinner-border spinner-border-sm" role="status" /> : "Verify OTP"}
                                </button>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ChangeStudentContactNumber;
